import {CSSProperties, InputHTMLAttributes, ReactNode, forwardRef, Ref} from 'react'
import {
    StyledInputWrapper,
    StyledDropzone,
    StyledInputFileButton,
    StyledInputFileWrapper
} from '@components/commons/input-file/style.ts'
import {Accept, useDropzone} from 'react-dropzone'
import {useTranslation} from '@/translations/i18n.tsx'
import {megabytesToBytes} from '@utilities/helpers.ts'
import {Label} from '@components/ui/label/Label.tsx'
import {InputStatusIcon} from '@components/ui/input-status-icon/InputStatusIcon.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {File02Icon} from '@/components/ui/icon/Icon'

export interface InputFileProps extends InputHTMLAttributes<HTMLInputElement> {
    acceptedFiles: Accept
    className?: string
    label?: string
    maxFiles?: number
    maxSizeInMB: number
    name?: string
    hideFilename?: boolean
    onAcceptFiles?: (file: File) => void
    placeholder?: string
    typeIcon?: ReactNode
    buttonIcon?: ReactNode
    touched?: boolean
    textButton?: string
    /**
     * how to use errorMessage with translations
     *  errorMessage={t(errors.text?.message || '')} this avoid undefined value problems
     */
    isOptional?: boolean
    errorMessage?: string
    helpText?: string
    inputSize?: 'md'
    width?: CSSProperties['width']
    hideButton?: boolean
}

export const InputFile = forwardRef<HTMLDivElement, InputFileProps>(
    (
        {
            acceptedFiles,
            className,
            errorMessage,
            helpText,
            inputSize = 'md',
            label,
            maxFiles,
            maxSizeInMB,
            name,
            onAcceptFiles,
            placeholder,
            hideFilename = false,
            textButton = '',
            touched = false,
            typeIcon,
            buttonIcon = <File02Icon />,
            isOptional,
            width = '100%',
            hideButton = false
        }: InputFileProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const {t} = useTranslation()
        const dropzoneState = useDropzone({
            accept: acceptedFiles,
            onDrop: acceptedFiles => {
                const [file] = acceptedFiles
                if (file) {
                    onAcceptFiles?.(file)
                }
            },
            maxFiles,
            maxSize: megabytesToBytes(maxSizeInMB)
        })

        return (
            <StyledInputFileWrapper className={className} ref={ref} $width={width}>
                <Flexbox direction="column" gap={1.5}>
                    {label && (
                        <Label htmlFor={name}>
                            {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                        </Label>
                    )}
                    <StyledDropzone state={dropzoneState} $width="100%">
                        <Flexbox gap={2.5}>
                            {!hideFilename && (
                                <StyledInputWrapper hasError={!!errorMessage} $size={inputSize}>
                                    {typeIcon}
                                    {<p className="file-name">{dropzoneState.acceptedFiles[0]?.name ?? placeholder}</p>}
                                </StyledInputWrapper>
                            )}
                            {!hideButton && (
                                <StyledInputFileButton variant="secondary">
                                    {buttonIcon}
                                    {textButton || t('commons:upload')}
                                </StyledInputFileButton>
                            )}
                        </Flexbox>
                        <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                    </StyledDropzone>
                    {errorMessage && <InputHelpText error={errorMessage} helpText={helpText} />}
                </Flexbox>
            </StyledInputFileWrapper>
        )
    }
)

InputFile.displayName = 'InputFile'
